module MapDrawing {
    /*
     * Adapted from mapbox/polylabel (ISC License)
     * Copyright (c) 2016 Mapbox
     *
     * Permission to use, copy, modify, and/or distribute this software for any
     * purpose with or without fee is hereby granted, provided that the above
     * copyright notice and this permission notice appear in all copies.
     *
     * THE SOFTWARE IS PROVIDED "AS IS" AND ISC DISCLAIMS ALL WARRANTIES WITH
     * REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
     * AND FITNESS. IN NO EVENT SHALL ISC BE LIABLE FOR ANY SPECIAL, DIRECT,
     * INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
     * LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE
     * OR OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
     * PERFORMANCE OF THIS SOFTWARE.
     */

    type PolylabelPoint = number[];
    type PolylabelRing = PolylabelPoint[];
    type PolylabelPolygon = PolylabelRing[];

    class PolylabelCell {
        x: number;
        y: number;
        h: number;
        d: number;
        max: number;

        constructor(x: number, y: number, h: number, polygon: PolylabelPolygon) {
            this.x = x;
            this.y = y;
            this.h = h;
            this.d = pointToPolygonDist(x, y, polygon);
            this.max = this.d + this.h * Math.SQRT2;
        }
    }

    export function getLabelPointInPolygon(poly: IGoogleMapsPolygon, precision: number = 0.000001): google.maps.LatLng {
        const polygon = toPolylabelPolygon(poly);
        if (!polygon.length || polygon[0].length < 3) {
            return null;
        }

        const point = polylabel(polygon, precision);
        if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
            return null;
        }

        return new google.maps.LatLng(point.y, point.x);
    }

    function toPolylabelPolygon(poly: IGoogleMapsPolygon): PolylabelPolygon {
        const polygon: PolylabelPolygon = [];

        poly.getPaths().forEach(path => {
            const ring: PolylabelRing = [];
            path.forEach(latLng => {
                ring.push([latLng.lng(), latLng.lat()]);
            });

            if (ring.length >= 3 && ring.every(point => Number.isFinite(point[0]) && Number.isFinite(point[1]))) {
                polygon.push(ring);
            }
        });

        return polygon;
    }

    function polylabel(polygon: PolylabelPolygon, precision: number = 1.0, debug: boolean = false): PolylabelCell {
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        for (const point of polygon[0]) {
            if (point[0] < minX) minX = point[0];
            if (point[1] < minY) minY = point[1];
            if (point[0] > maxX) maxX = point[0];
            if (point[1] > maxY) maxY = point[1];
        }

        const width = maxX - minX;
        const height = maxY - minY;
        const cellSize = Math.max(precision, Math.min(width, height));

        if (cellSize === precision) {
            return new PolylabelCell(minX, minY, 0, polygon);
        }

        const cellQueue = new ArrayHelper.TinyQueue<PolylabelCell>([], (a, b) => b.max - a.max);
        let bestCell = getCentroidCell(polygon);

        const bboxCell = new PolylabelCell(minX + width / 2, minY + height / 2, 0, polygon);
        if (bboxCell.d > bestCell.d) {
            bestCell = bboxCell;
        }

        let numProbes = 2;

        const potentiallyQueue = (x: number, y: number, h: number) => {
            const cell = new PolylabelCell(x, y, h, polygon);
            numProbes++;

            if (cell.max > bestCell.d + precision) {
                cellQueue.push(cell);
            }

            if (cell.d > bestCell.d) {
                bestCell = cell;
                if (debug) {
                    console.log(`found best ${Math.round(1e4 * cell.d) / 1e4} after ${numProbes} probes`);
                }
            }
        };

        let h = cellSize / 2;
        for (let x = minX; x < maxX; x += cellSize) {
            for (let y = minY; y < maxY; y += cellSize) {
                potentiallyQueue(x + h, y + h, h);
            }
        }

        while (cellQueue.length) {
            const cell = cellQueue.pop();
            if (cell.max - bestCell.d <= precision) {
                continue;
            }

            h = cell.h / 2;
            potentiallyQueue(cell.x - h, cell.y - h, h);
            potentiallyQueue(cell.x + h, cell.y - h, h);
            potentiallyQueue(cell.x - h, cell.y + h, h);
            potentiallyQueue(cell.x + h, cell.y + h, h);
        }

        if (debug) {
            console.log(`num probes: ${numProbes}\nbest distance: ${bestCell.d}`);
        }

        return bestCell;
    }

    function pointToPolygonDist(x: number, y: number, polygon: PolylabelPolygon): number {
        let inside = false;
        let minDistSq = Infinity;

        for (const ring of polygon) {
            for (let i = 0, len = ring.length, j = len - 1; i < len; j = i++) {
                const a = ring[i];
                const b = ring[j];

                if ((a[1] > y !== b[1] > y) &&
                    (x < (b[0] - a[0]) * (y - a[1]) / (b[1] - a[1]) + a[0])) {
                    inside = !inside;
                }

                minDistSq = Math.min(minDistSq, getSegDistSq(x, y, a, b));
            }
        }

        if (minDistSq === 0) {
            return 0;
        }

        return (inside ? 1 : -1) * Math.sqrt(minDistSq);
    }

    function getCentroidCell(polygon: PolylabelPolygon): PolylabelCell {
        let area = 0;
        let x = 0;
        let y = 0;
        const points = polygon[0];

        for (let i = 0, len = points.length, j = len - 1; i < len; j = i++) {
            const a = points[i];
            const b = points[j];
            const f = a[0] * b[1] - b[0] * a[1];
            x += (a[0] + b[0]) * f;
            y += (a[1] + b[1]) * f;
            area += f * 3;
        }

        const centroid = area === 0 ? null : new PolylabelCell(x / area, y / area, 0, polygon);
        if (centroid === null || centroid.d < 0) {
            return new PolylabelCell(points[0][0], points[0][1], 0, polygon);
        }

        return centroid;
    }

    function getSegDistSq(px: number, py: number, a: PolylabelPoint, b: PolylabelPoint): number {
        let x = a[0];
        let y = a[1];
        let dx = b[0] - x;
        let dy = b[1] - y;

        if (dx !== 0 || dy !== 0) {
            const t = ((px - x) * dx + (py - y) * dy) / (dx * dx + dy * dy);

            if (t > 1) {
                x = b[0];
                y = b[1];
            } else if (t > 0) {
                x += dx * t;
                y += dy * t;
            }
        }

        dx = px - x;
        dy = py - y;

        return dx * dx + dy * dy;
    }
}
